import { useCallback, useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { ReactComponent as ExternalLinkIcon } from "../../../assets/external_link.svg";
import { BASIC_BLACK, BASIC_ORANGE } from "../../../constants/colors";
import { PageRoutes } from "../../../constants/routes";
import { AuthType } from "../../../ducks/auth";
import { validateEmail } from "../../../utils/auth";
import { Button } from "../../atoms/Button";
import { Checkbox } from "../../atoms/Checkbox";
import { Input } from "../../atoms/Input";
import { Link } from "../../atoms/Link";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  showSms: boolean;
  handleEmailError: () => void;
  handleSubmit: (type: AuthType, value: string) => void;
  handleSubmitEmail: (email: string, emailConfirmation: string) => void;
};

export const CustomerInfoForm: React.FC<Props> = ({
  showSms,
  handleEmailError,
  handleSubmit,
  handleSubmitEmail,
}) => {
  const schema = useMemo(() => {
    if (showSms) {
      return z
        .object({
          email: z.union([z.literal(""), z.string()]),
          phoneNumber: z.string(),
          hasChecked: z.boolean(),
        })
        .refine((data) => {
          return (data.email || data.phoneNumber) && data.hasChecked;
        });
    } else {
      return z
        .object({
          email: z.union([z.literal(""), z.string()]),
          emailConfirmation: z.union([z.literal(""), z.string()]),
          hasChecked: z.boolean(),
        })
        .refine((data) => {
          return (
            data.email.length > 0 &&
            data.email == data.emailConfirmation &&
            data.hasChecked
          );
        });
    }
  }, [showSms]);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [hasClicked, setHasClicked] = useState(false);

  const setHasChecked = useCallback(
    (checked: boolean) => {
      if (!methods) return;

      methods.setValue("hasChecked", checked, { shouldValidate: true });
    },
    [methods],
  );

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        email: "",
        emailConfirmation: "",
        phoneNumber: "",
        hasChecked: false,
      }}
      onSubmit={(data) => {
        if (data.email && !validateEmail(data.email)) return handleEmailError();

        if (showSms) {
          handleSubmit(
            showSms
              ? data.email.length > 0
                ? AuthType.Email
                : AuthType.PhoneNumber
              : AuthType.Email,
            // @ts-expect-error
            data.email || data.phoneNumber,
          );
        } else {
          // @ts-expect-error
          handleSubmitEmail(data.email, data.emailConfirmation);
        }
      }}
    >
      {showSms ? (
        <>
          <TitleContent>
            メールアドレス、または携帯電話番号のどちらかを入力して下さい
          </TitleContent>
          <InputContainer>
            <Input
              name="email"
              type="email"
              label="メールアドレス"
              placeholder="メールアドレス"
              onChange={setEmail}
              disabled={phoneNumber !== ""}
            />
          </InputContainer>
          <InputContainer>
            <TitleContent2>または</TitleContent2>
            <Input
              name="phoneNumber"
              type="text"
              label="携帯電話番号"
              placeholder="携帯電話番号"
              onChange={setPhoneNumber}
              disabled={email !== ""}
            />
          </InputContainer>
          <ContentWrapper>
            <TitleContent>※メールアドレスを送信する場合</TitleContent>
            <BodyText>
              <ul>
                <li>
                  ・ドメイン指定受信をご利用の方は「@joyfull-weborder.jp」を追加してメールが受信できるようにしてください。
                </li>
                <li>
                  ・迷惑メールフォルダに振り分けられていないかご確認ください。
                </li>
                <li>・登録したメールアドレスに誤りがないかご確認ください。</li>
                <li>
                  ・メールボックスが容量一杯になっていないかご確認ください。
                </li>
              </ul>
            </BodyText>
            <Content>
              上記のご対応をいただいてもメールが届かない場合は、Webメール（GmailやYahoo!メールなど）のご使用や、携帯電話番号をご入力の上、SMS認証をご検討ください。
            </Content>
          </ContentWrapper>
          <ContentWrapper>
            <TitleContent>※携帯電話番号を送信する場合</TitleContent>
            <Content>
              携帯電話番号の入力に誤りがないかご確認ください。携帯電話番号を確認した上でSMSが届かない場合は、以下をご確認ください。
            </Content>
            <BodyText>
              <ul>
                <li>
                  ・モバイル通信ができない状態になっていないかご確認ください。
                </li>
                <li>・SMSの受信拒否設定をされていないかご確認ください。</li>
                <li>
                  ・SMSをご利用できない電話・PHS・携帯電話ではSMS認証がご利用いただけません。
                </li>
              </ul>
            </BodyText>
          </ContentWrapper>
        </>
      ) : (
        <>
          <InputContainer>
            <Input
              name="email"
              type="email"
              label="メールアドレス"
              placeholder="メールアドレス"
            />
          </InputContainer>
          <InputContainer>
            <Input
              name="emailConfirmation"
              type="email"
              label="メールアドレス再入力"
              placeholder="メールアドレス再入力"
            />
          </InputContainer>
          <ContentWrapper>
            <TitleContent>※メールアドレスを送信する場合</TitleContent>
            <BodyText>
              <ul>
                <li>
                  ・ドメイン指定受信をご利用の方は「@joyfull-weborder.jp」を追加してメールが受信できるようにしてください。
                </li>
                <li>
                  ・迷惑メールフォルダに振り分けられていないかご確認ください。
                </li>
                <li>・登録したメールアドレスに誤りがないかご確認ください。</li>
                <li>
                  ・メールボックスが容量一杯になっていないかご確認ください。
                </li>
              </ul>
            </BodyText>
            <Content>
              上記のご対応をいただいてもメールが届かない場合は、Webメール（GmailやYahoo!メールなど）の使用をご検討ください。
            </Content>
          </ContentWrapper>
        </>
      )}

      <CheckboxWrapper>
        <InlineContainer>
          <LinkWrapper>
            <Link
              href={PageRoutes.LEGAL_FOR_USE_QUERY.replace(":query", "close")}
              text="利用規約及び個人情報保護方針"
              color={BASIC_BLACK}
              newTab
              fontFamily="Hiragino Sans"
              onClick={() => setHasClicked(true)}
              size="md"
              underline
            />
            <IconContainer>
              <ExternalLinkIcon />
            </IconContainer>
          </LinkWrapper>
          <Text>
            をご確認の上、同意された方は「送信する」ボタンを押してください。
          </Text>
        </InlineContainer>
        <CheckboxContainer>
          <Checkbox
            text="同意する"
            disabled={!hasClicked}
            onChange={setHasChecked}
          />
        </CheckboxContainer>
        <RedText>
          ※利用規約及び個人情報保護方針をご確認いただかないと「同意する」はチェックできません
        </RedText>
      </CheckboxWrapper>

      <ButtonWrapper>
        <Button btnStyle="primary" text="送信する" type="submit" />
      </ButtonWrapper>
    </FormTemplate>
  );
};

const TitleContent = styled.p`
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  margin: 0 0 16px;
  font-family: "Hiragino Sans";
`;

const TitleContent2 = styled.p`
  text-align: left;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  margin: 0 0 5px;
  font-family: "Hiragino Sans";
`;

const ContentWrapper = styled.div`
  padding: 20px 5px 5px;
`;

const Wrapper = styled.div`
  overflow: hidden;
`;

const Content = styled.div`
  text-align: left;
  font-weight: 300;
  margin: 0 0 15px;
  padding: 5px 0 0;
  font-size: 15px;
  font-family: "Hiragino Sans";
`;

const InputContainer = styled.div`
  margin: 0 0 20px;
`;
const BodyText = styled.div`
  ul {
    font-size: 15px;
    font-weight: 300;
    line-height: 1.7em;
    margin: 0 0 0 1em;
    padding: 0;

    li {
      list-style: none;
      text-indent: -1em;
      margin: 0 0 0.5em 0;
      font-family: "Hiragino Sans";
    }
  }
`;

const InlineContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  text-align: left;
  justify-content: flex-start;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 40px 0 12px;
  button {
    font-family: "Hiragino Sans";
    font-weight: 600;
    line-height: 1em;
    height: auto;
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  margin: 28px 0 0;
  padding: 0 5px;
`;

const CheckboxContainer = styled.div`
  margin-top: 20px;
`;

const RedText = styled.p`
  display: inline-block;
  padding: 0 8px 5px 0;
  margin-top: 15px;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.7em;
  letter-spacing: 0.1em;
  font-family: "Hiragino Sans";
  color: ${BASIC_ORANGE};
`;

const LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 0 5px;
`;
const IconContainer = styled(ExternalLinkIcon)`
  margin-right: 8px;
  width: 16px;
  height: 16px;
`;

const Text = styled.span`
  text-align: left;
  margin: 0;
  white-space: nowrap;
  font-weight: 300;
  font-family: "Hiragino Sans";
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
`;
